// Account identity is independent of the app theme. Missing/unavailable photos
// use Link's bundled ghost; groups can explicitly keep their initials.

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import {
  forwardRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from "react";

import { cn } from "../lib/cn";

export const Avatar = forwardRef<
  ElementRef<typeof AvatarPrimitive.Root>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "avatar-frame relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

export const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

export const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & { variant?: "ghost" | "initials" }
>(({ className, children, variant = "ghost", ...props }, ref) => {
  const [ghostUnavailable, setGhostUnavailable] = useState(false);
  const showGhost = variant === "ghost" && !ghostUnavailable;
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-[inherit] text-sm font-semibold",
        showGhost ? "avatar-fallback-ghost" : "avatar-fallback-strawberry",
        className,
      )}
      {...props}
    >
      {showGhost ? <>
        <img src="/images/link-ghost.png" alt="" aria-hidden="true" draggable={false}
          decoding="async" className="avatar-default-ghost" onError={() => setGhostUnavailable(true)} />
        <span className="sr-only">{children}</span>
      </> : children}
    </AvatarPrimitive.Fallback>
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
